import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Home, Wifi, WifiOff, Plus, Trash2, Eye, EyeOff, Edit2,
  CheckCircle2, AlertCircle, RefreshCw, Download, ChevronRight,
  Zap, Mic, Shield, Clock, Settings, ExternalLink, X, Activity,
  Lightbulb, Thermometer, Lock, Cpu
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const USE_CASES = [
  { icon: "💡", title: "Luces inteligentes", desc: "Enciende, apaga o dimea cualquier luz con una frase natural." },
  { icon: "🌡️", title: "Clima & temperatura", desc: "Controla aire acondicionado y calefacción por voz o texto." },
  { icon: "🔒", title: "Seguridad", desc: "Bloquea o desbloquea cerraduras, activa alarmas desde el chat." },
  { icon: "🎬", title: "Rutinas automáticas", desc: "\"Modo noche\" — apaga todo, baja persianas, ajusta temperatura." },
  { icon: "📊", title: "Estado del hogar", desc: "Pregunta qué dispositivos están activos ahora mismo." },
  { icon: "⚡", title: "Enchufes & energía", desc: "Controla cualquier enchufe inteligente conectado a Homebridge." },
];

const INSTALL_STEPS = [
  { n: 1, title: "Instala Homebridge", desc: "En tu Raspberry Pi, Mac o servidor. Visita homebridge.io para la guía oficial.", link: "https://homebridge.io" },
  { n: 2, title: "Crea un túnel público", desc: "Usa Cloudflare Tunnel o ngrok para exponer Homebridge a internet con una URL segura.", link: "https://developers.cloudflare.com/cloudflare-one/connections/connect-apps/" },
  { n: 3, title: "Obtén tu API token", desc: "En Homebridge UI → Admin → Manage API Tokens → crea un token con permisos de lectura y escritura.", link: null },
  { n: 4, title: "Conecta con OkHome", desc: "Pega la URL de tu túnel y el token aquí abajo. OkBot descubrirá tus dispositivos automáticamente.", link: null },
];

export default function OkHomeModule({ user }) {
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [form, setForm] = useState({ name: "", url: "", port: 8581, token: "", description: "" });
  const [editId, setEditId] = useState(null);
  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [activeTab, setActiveTab] = useState("status"); // status | devices | install

  useEffect(() => { if (user?.id) loadInstances(); }, [user?.id]);

  const loadInstances = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/homebridge/user/${user.id}/instances`);
      setInstances(r.data.instances || []);
    } catch (e) {
      // no instances yet — that's fine
    } finally {
      setLoading(false);
    }
  };

  const loadDevices = async () => {
    setLoadingDevices(true);
    try {
      const r = await axios.get(`${API}/homebridge/user/${user.id}/devices`);
      setDevices(r.data.devices || []);
    } catch (e) {
      toast.error("No se pudieron cargar dispositivos");
    } finally {
      setLoadingDevices(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "devices" && devices.length === 0) loadDevices();
  };

  const testConn = async () => {
    if (!form.url || !form.token) { toast.error("Ingresa URL y token primero"); return; }
    setTesting(true); setTestResult(null);
    try {
      const r = await axios.post(`${API}/homebridge/user/${user.id}/test`, { url: form.url, token: form.token });
      setTestResult(r.data);
    } catch (e) {
      setTestResult({ ok: false, error: "Error de red" });
    } finally {
      setTesting(false);
    }
  };

  const saveInstance = async () => {
    if (!form.name || !form.url || !form.token) { toast.error("Nombre, URL y token son requeridos"); return; }
    try {
      await axios.post(`${API}/homebridge/user/${user.id}/instances`, { ...form, id: editId || undefined });
      toast.success(editId ? "Instancia actualizada" : "Instancia conectada ✓");
      setShowForm(false); setForm({ name: "", url: "", port: 8581, token: "", description: "" }); setEditId(null); setTestResult(null);
      loadInstances();
    } catch (e) {
      toast.error("Error al guardar instancia");
    }
  };

  const deleteInst = async (id) => {
    if (!window.confirm("¿Eliminar esta instancia?")) return;
    try {
      await axios.delete(`${API}/homebridge/user/${user.id}/instances/${id}`);
      toast.success("Instancia eliminada");
      loadInstances();
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  const hasInstances = instances.length > 0;

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-6">

      {/* ── Hero ── */}
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#0F172A] to-[#1E3A5F] p-6 md:p-8">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:"url('/images/okhome-hero-bg.png')", backgroundSize:"cover"}}/>
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-3 py-1 rounded-full text-xs text-white/80 font-medium mb-4">
            <span className="w-2 h-2 rounded-full bg-[#E53935] animate-pulse"/> POWERED BY HOMEBRIDGE
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-2">
            Ok<span className="text-[#E53935]">Home</span>
          </h1>
          <p className="text-white/70 text-base max-w-lg mb-6">Tu hogar inteligente, controlado por voz y texto natural desde OkBot.</p>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => { setShowForm(true); setActiveTab("status"); }}
              className="bg-[#E53935] hover:bg-[#C62828] text-white px-5 py-2.5 rounded-xl font-semibold flex items-center gap-2 transition-all shadow-lg">
              <Zap className="w-4 h-4"/>
              {hasInstances ? "Agregar instancia" : "Conectar mi Homebridge"}
            </button>
            <button onClick={() => handleTabChange("install")}
              className="bg-white/10 hover:bg-white/20 text-white border border-white/20 px-5 py-2.5 rounded-xl font-medium flex items-center gap-2 transition-all">
              <Download className="w-4 h-4"/> Ver instalación
            </button>
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="flex gap-2 border-b border-[var(--border)]">
        {[
          { id: "status", label: "Estado de Conexión", icon: Activity },
          { id: "devices", label: "Dispositivos", icon: Cpu },
          { id: "usecases", label: "Casos de Uso", icon: Lightbulb },
          { id: "install", label: "Instalación", icon: Settings },
        ].map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => handleTabChange(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                activeTab === t.id
                  ? "border-[#E53935] text-[#E53935]"
                  : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              }`}>
              <Icon className="w-4 h-4 hidden sm:block"/>
              {t.label}
            </button>
          );
        })}
      </div>

      {/* ── Status Tab ── */}
      {activeTab === "status" && (
        <div className="space-y-4">
          {loading ? (
            <div className="glass-panel p-8 flex justify-center"><RefreshCw className="w-6 h-6 animate-spin text-[#E53935]"/></div>
          ) : !hasInstances ? (
            <div className="glass-panel p-10 text-center">
              <div className="w-16 h-16 bg-[var(--surface-alt)] rounded-2xl flex items-center justify-center mx-auto mb-4 border border-[var(--border)]">
                <Home className="w-8 h-8 text-[var(--text-tertiary)]"/>
              </div>
              <h3 className="font-semibold text-[var(--text-primary)] mb-2">Sin instancias conectadas</h3>
              <p className="text-[var(--text-secondary)] text-sm mb-6 max-w-sm mx-auto">Conecta tu instalación de Homebridge para empezar a controlar tu hogar desde el chat.</p>
              <button onClick={() => setShowForm(true)} className="bg-[#E53935] hover:bg-[#C62828] text-white px-6 py-2.5 rounded-xl font-semibold transition-all">
                Conectar Homebridge
              </button>
            </div>
          ) : (
            instances.map((inst, i) => (
              <div key={i} className="glass-panel p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-[#0EA5E9]/10 border border-[#0EA5E9]/20 flex items-center justify-center shrink-0">
                    <Home className="w-5 h-5 text-[#0EA5E9]"/>
                  </div>
                  <div>
                    <h3 className="font-semibold text-[var(--text-primary)]">{inst.name}</h3>
                    <p className="text-xs text-[var(--text-secondary)] font-mono mt-0.5">{inst.url}</p>
                    {inst.description && <p className="text-xs text-[var(--text-tertiary)] mt-1">{inst.description}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-500/10 text-green-400">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"/> Conectado
                  </span>
                  <button onClick={() => { setForm({name:inst.name,url:inst.url||"",port:inst.port||8581,token:"",description:inst.description||""}); setEditId(inst.id); setShowForm(true); }}
                    className="p-2 rounded-lg text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-alt)] transition-colors">
                    <Edit2 className="w-4 h-4"/>
                  </button>
                  <button onClick={() => deleteInst(inst.id)}
                    className="p-2 rounded-lg text-[var(--text-secondary)] hover:text-[#E53935] hover:bg-red-500/10 transition-colors">
                    <Trash2 className="w-4 h-4"/>
                  </button>
                </div>
              </div>
            ))
          )}
          {hasInstances && (
            <button onClick={() => setShowForm(true)}
              className="w-full border-2 border-dashed border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[#0EA5E9]/40 rounded-xl p-4 flex items-center justify-center gap-2 text-sm font-medium transition-colors">
              <Plus className="w-4 h-4"/> Agregar otra instancia
            </button>
          )}
        </div>
      )}

      {/* ── Devices Tab ── */}
      {activeTab === "devices" && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <button onClick={loadDevices} className="text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] flex items-center gap-1.5 transition-colors">
              <RefreshCw className={`w-4 h-4 ${loadingDevices ? "animate-spin" : ""}`}/> Sincronizar
            </button>
          </div>
          {loadingDevices ? (
            <div className="glass-panel p-8 flex justify-center"><RefreshCw className="w-6 h-6 animate-spin text-[#E53935]"/></div>
          ) : devices.length === 0 ? (
            <div className="glass-panel p-8 text-center text-[var(--text-secondary)]">
              {hasInstances ? "No se encontraron dispositivos. ¿Homebridge está activo?" : "Conecta una instancia primero para ver dispositivos."}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {devices.map((d, i) => (
                <div key={i} className="glass-panel p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[var(--surface-alt)] flex items-center justify-center text-lg border border-[var(--border)] shrink-0">
                    {d.type?.includes("Light") ? "💡" : d.type?.includes("Thermostat") ? "🌡️" : d.type?.includes("Lock") ? "🔒" : d.type?.includes("Switch") ? "⚡" : "📱"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-[var(--text-primary)] truncate">{d.name}</h4>
                    <p className="text-xs text-[var(--text-secondary)]">{d.room} · {d.instance_name}</p>
                  </div>
                  <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${d.on ? "bg-green-400" : "bg-[var(--border)]"}`}/>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Use Cases Tab ── */}
      {activeTab === "usecases" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {USE_CASES.map((uc, i) => (
            <div key={i} className="glass-panel p-5 space-y-2">
              <span className="text-3xl">{uc.icon}</span>
              <h3 className="font-semibold text-[var(--text-primary)]">{uc.title}</h3>
              <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{uc.desc}</p>
            </div>
          ))}
        </div>
      )}

      {/* ── Install Tab ── */}
      {activeTab === "install" && (
        <div className="space-y-4">
          {INSTALL_STEPS.map((step, i) => (
            <div key={i} className="glass-panel p-5 flex gap-4">
              <div className="w-9 h-9 rounded-xl bg-[#E53935]/10 border border-[#E53935]/20 flex items-center justify-center shrink-0 font-bold text-[#E53935]">
                {step.n}
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-[var(--text-primary)] mb-1">{step.title}</h3>
                <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{step.desc}</p>
                {step.link && (
                  <a href={step.link} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-[#0EA5E9] hover:underline mt-2">
                    Ver documentación <ExternalLink className="w-3 h-3"/>
                  </a>
                )}
              </div>
            </div>
          ))}
          <div className="glass-panel p-5 border border-[#0EA5E9]/20 bg-[#0EA5E9]/5">
            <div className="flex items-start gap-3">
              <Download className="w-5 h-5 text-[#0EA5E9] shrink-0 mt-0.5"/>
              <div>
                <h3 className="font-semibold text-[var(--text-primary)] mb-1">Descargar Homebridge</h3>
                <p className="text-sm text-[var(--text-secondary)] mb-3">Disponible para Raspberry Pi, macOS, Linux y Docker.</p>
                <a href="https://homebridge.io/how-to-install-homebridge/" target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#C62828] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all">
                  Ir a homebridge.io <ExternalLink className="w-3 h-3"/>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Instance Form Modal ── */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl p-6 w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-[var(--text-primary)]">
                {editId ? "Editar instancia" : "Conectar Homebridge"}
              </h2>
              <button onClick={() => { setShowForm(false); setTestResult(null); }} className="p-1 text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                <X className="w-5 h-5"/>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">Nombre</label>
                <input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                  placeholder="Ej: Casa Principal" className="input-field w-full"/>
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">URL del túnel</label>
                  <input value={form.url} onChange={e => setForm({...form, url: e.target.value})}
                    placeholder="https://tu-tunnel.trycloudflare.com" className="input-field w-full font-mono text-sm"/>
                </div>
                <div className="w-24">
                  <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">Puerto</label>
                  <input type="number" value={form.port} onChange={e => setForm({...form, port: parseInt(e.target.value)})}
                    className="input-field w-full font-mono text-sm"/>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">Token de API</label>
                <div className="relative">
                  <input type={showToken ? "text" : "password"} value={form.token}
                    onChange={e => setForm({...form, token: e.target.value})}
                    placeholder="eyJhbGci..." className="input-field w-full font-mono text-sm pr-10"/>
                  <button onClick={() => setShowToken(!showToken)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]">
                    {showToken ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">Descripción <span className="font-normal opacity-60">(Opcional)</span></label>
                <textarea rows={2} value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                  placeholder="Notas sobre esta conexión..." className="input-field w-full resize-none"/>
              </div>

              {testResult && (
                <div className={`flex items-start gap-3 p-3 rounded-xl border ${testResult.ok ? "bg-green-500/10 border-green-500/20" : "bg-red-500/10 border-red-500/20"}`}>
                  {testResult.ok
                    ? <CheckCircle2 className="w-5 h-5 text-green-400 shrink-0 mt-0.5"/>
                    : <AlertCircle className="w-5 h-5 text-[#E53935] shrink-0 mt-0.5"/>
                  }
                  <div>
                    <p className={`text-sm font-semibold ${testResult.ok ? "text-green-400" : "text-[#E53935]"}`}>
                      {testResult.ok ? `Conexión exitosa · Homebridge v${testResult.version} · ${testResult.ms}ms` : `Error: ${testResult.error}`}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button onClick={() => { setShowForm(false); setTestResult(null); }}
                  className="flex-1 border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--surface-alt)] py-2.5 rounded-xl text-sm font-medium transition-colors">
                  Cancelar
                </button>
                <button onClick={testConn} disabled={testing}
                  className="border border-[#0EA5E9]/50 text-[#0EA5E9] hover:bg-[#0EA5E9]/10 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50">
                  <Activity className="w-4 h-4"/> {testing ? "Probando..." : "Probar"}
                </button>
                <button onClick={saveInstance}
                  className="flex-1 bg-[#E53935] hover:bg-[#C62828] text-white py-2.5 rounded-xl text-sm font-semibold transition-all shadow-sm">
                  Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
